import { isQualificationScheme } from "../../sourcing-events/buyer/wizard/utils/procedure";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Table,
  Tbody,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChangeGovernedTerm,
  ChangeReasonCode,
  type EventRevisionDto,
  type EventRevisionPatchDto,
  type FieldErrorDto,
  type SourcingEventDto,
  type ViewDocumentAsOwnerDto,
  useCancelEventChangeMutation,
  useGetSourcingEventQuery,
  useSubmitEventChangeMutation,
  useUpdateEventChangeMutation,
} from "../../../autogen/bff-api";
import { DropTarget } from "../../../common/documents/UppyUploader/DropTarget";
import {
  FileRow,
  type IncompleteFileUpload,
  isIncompleteFileUpload,
} from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../common/editing/save-coordinator";
import { useExclusiveAction } from "../../../common/hooks/useExclusiveAction";
import { useApiError } from "../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { DoffinRefusalAlert, doffinRefusal } from "../result-notice/DoffinRefusalAlert";
import { DoffinNoAnswerAlert } from "../result-notice/DoffinNoAnswerAlert";
import { type DoffinNoAnswer, doffinNoAnswer } from "../result-notice/doffinNoAnswer";
import { ThemedDateTimePicker } from "../../../common/input/DateTimeSelectors/ThemedDateTimePicker";
import { ThemedDatePicker } from "../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { endOfDay } from "../../sourcing-events/buyer/wizard/components/schemeValidityDates";
import { TipTap } from "../../../common/input/TipTap/TipTap";
import { deadlineEditingOf } from "./changeWindow";
import { CHANGE_VALUE_FIELDS, ChangeValueFields, focusChangeValueField } from "./ChangeValueFields";
import { isApiErrorDto } from "../../../common/errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { isNegotiationWithoutNotice } from "../../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";

interface Props {
  event: SourcingEventDto;
  draft: EventRevisionDto;
  isOpen: boolean;
  onClose: () => void;
}

const NO_DOCUMENTS: ViewDocumentAsOwnerDto[] = [];

export const EventChangeEditor = ({ event, draft, isOpen, onClose }: Props) => {
  const [update] = useUpdateEventChangeMutation();
  const editorErrorConfig = useApiErrorEditorConfig();

  useCreateEditor<EventRevisionDto, EventRevisionPatchDto>(draft.id, {
    initialState: draft,
    save: (patch) =>
      update({ eventId: event.id, revisionId: draft.id, eventRevisionPatchDto: patch }).unwrap(),
    ...editorErrorConfig,
  });

  return <EventChangeEditorBody event={event} draft={draft} isOpen={isOpen} onClose={onClose} />;
};

const EventChangeEditorBody = ({ event, draft, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const apiError = useApiError();
  const { state, updateField, fieldStatus, fieldError, flushPending } =
    useEditor<EventRevisionDto, EventRevisionPatchDto>(draft.id);
  const [valueRefusals, setValueRefusals] = useState<FieldErrorDto[]>([]);
  const [submit, { isLoading: isSubmitting }] = useSubmitEventChangeMutation();
  const discardConfirmation = useConfirmationModal();
  const [refusal, setRefusal] = useState<FieldErrorDto[] | null>(null);
  const [noAnswer, setNoAnswer] = useState<DoffinNoAnswer | null>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const refused = valueRefusals[0];
    if (refused) focusChangeValueField(dialogRef.current, refused.field);
  }, [valueRefusals]);

  useEffect(() => {
    if (refusal || noAnswer) outcomeRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [refusal, noAnswer]);
  const [cancel, { isLoading: isCanceling }] = useCancelEventChangeMutation();
  const { refetch } = useGetSourcingEventQuery({ eventId: event.id });

  const { uppy, allFiles, setDocuments, removeUpload } = useUppyUploader({
    entityType: "SourcingEventV2",
    entityId: event.id,
    reloadEntity: refetch,
  });
  const savedDocuments = event.eventData.documents ?? NO_DOCUMENTS;
  useEffect(() => {
    setDocuments(savedDocuments);
  }, [savedDocuments, setDocuments]);
  const pendingUploads = allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[];

  const privateContext = event.eventContext.type === "Private" ? event.eventContext : null;
  const withoutNotice =
    event.eventContext.type === "Public" && isNegotiationWithoutNotice(event.eventContext.procedureType);
  const changesRequest = privateContext != null || withoutNotice;
  const invitedCount =
    event.eventContext.type === "Public"
      ? event.eventContext.negotiationWithoutNotice?.invitedOrganizationIds?.length ?? 0
      : 0;
  const recipientCount = privateContext
    ? privateContext.organizationInvites.length
    : withoutNotice
      ? invitedCount
      : event.supplierRegistrations?.length ?? 0;
  const deadline = deadlineEditingOf(event);
  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";

  const eventDocuments = savedDocuments;
  const includedDocumentIds = state.requestDocumentIds ?? [];

  // Documents uploaded while the editor is open join the change automatically; documents that
  // existed when it opened keep whatever inclusion the draft was forked with.
  const seenDocumentIdsRef = useRef<Set<string>>(new Set(eventDocuments.map((doc) => doc.id)));
  useEffect(() => {
    const fresh = eventDocuments.filter((doc) => !seenDocumentIdsRef.current.has(doc.id));
    if (fresh.length === 0) return;
    fresh.forEach((doc) => seenDocumentIdsRef.current.add(doc.id));
    updateField({ requestDocumentIds: [...includedDocumentIds, ...fresh.map((doc) => doc.id)] });
  }, [eventDocuments, includedDocumentIds, updateField]);

  const toggleDocument = (documentId: string, include: boolean) => {
    const next = include
      ? [...includedDocumentIds, documentId]
      : includedDocumentIds.filter((id) => id !== documentId);
    updateField({ requestDocumentIds: next });
  };

  const [handleSubmit, isSending] = useExclusiveAction(async () => {
    dialogRef.current?.focus();
    setRefusal(null);
    setNoAnswer(null);
    try {
      await flushPending();
      await submit({ eventId: event.id, revisionId: draft.id }).unwrap();
      toast({
        title: privateContext
          ? t("The change has been published")
          : withoutNotice
            ? t("The change has been sent")
            : t("The change notice has been submitted"),
        status: "success",
      });
      onClose();
    } catch (e) {
      const breaches = doffinRefusal(e);
      const noAnswer = doffinNoAnswer(e);
      const refusedValues = valueRefusalsOf(e);
      setValueRefusals(refusedValues);
      if (breaches) setRefusal(breaches);
      else if (noAnswer) setNoAnswer(noAnswer);
      else if (!refusesOnlyValues(e, refusedValues)) apiError.trigger(e);
    }
  });

  const isLeaving = isSending || isCanceling;

  const confirmDiscard = () =>
    discardConfirmation.show({
      title: String(t("Discard the change?")),
      description: t(
        "You discard the change and everything you have filled in: new values, «What changed?», the new deadline and the choice of competition documents. This cannot be undone.",
      ),
      confirmButtonText: String(t("Discard change")),
      confirmButtonColor: "red",
      onConfirmed: handleCancel,
    });

  const handleCancel = async () => {
    try {
      await cancel({ eventId: event.id, revisionId: draft.id }).unwrap();
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
      closeOnOverlayClick={!isLeaving}
      closeOnEsc={!isLeaving}
    >
      <ModalOverlay />
      <ModalContent ref={dialogRef}>
        <ModalHeader>{changesRequest ? t("Change the request") : t("Change the announcement")}</ModalHeader>
        <ModalCloseButton isDisabled={isLeaving} />
        <ModalBody>
          <Stack spacing="5">
            <Box ref={outcomeRef} hidden={!refusal && !noAnswer}>
              {refusal && (
                <DoffinRefusalAlert
                  breaches={refusal}
                  heading={t("Doffin refused the change notice. Nothing has been announced.")}
                  advice={t("Correct the change and send it again, or contact support.")}
                  borderRadius="md"
                  fontSize="sm"
                />
              )}

              {noAnswer && <DoffinNoAnswerAlert noAnswer={noAnswer} borderRadius="md" fontSize="sm" />}
            </Box>

            {event.changeGovernedTerm === ChangeGovernedTerm.SchemeValidity ? (
              <FormControl>
                <FormLabel>
                  {t("New end of validity")} {isSaving("schemeValidTo") && <Spinner size="xs" />}
                </FormLabel>
                <ThemedDatePicker
                  date={state.schemeValidTo}
                  onChange={(date) => updateField({ schemeValidTo: date ? endOfDay(date) : null })}
                  minDate={deadline.earliestSelectable}
                />
                <FormHelperText>
                  {isQualificationScheme(event.eventContext.type === "Public" ? event.eventContext.procedureType : null)
                    ? t("The validity of the qualification system can only be extended, never shortened")
                    : t("The validity of the purchasing system can only be extended, never shortened")}
                </FormHelperText>
              </FormControl>
            ) : event.changeGovernedTerm === ChangeGovernedTerm.RequestDeadline ? (
              <FormControl>
                <FormLabel>
                  {t("New deadline for requests to participate")}{" "}
                  {isSaving("requestDeadline") && <Spinner size="xs" />}
                </FormLabel>
                <ThemedDateTimePicker
                  date={state.requestDeadline}
                  onChange={(iso) => updateField({ requestDeadline: iso ?? null })}
                  laterThan={deadline.earliestSelectable}
                />
                <FormHelperText>
                  {t("The deadline for requests to participate can only be extended, never shortened")}
                </FormHelperText>
              </FormControl>
            ) : (
              <FormControl>
                <FormLabel>
                  {t("New bid deadline")} {isSaving("bidSubmissionDeadline") && <Spinner size="xs" />}
                </FormLabel>
                <ThemedDateTimePicker
                  date={state.bidSubmissionDeadline}
                  onChange={(iso) => updateField({ bidSubmissionDeadline: iso ?? null })}
                  laterThan={deadline.earliestSelectable}
                />
                <FormHelperText>
                  {deadline.canReopen
                    ? t("The bid deadline has passed. Pick a new one ahead in time to reopen the competition.")
                    : t("The bid deadline can only be extended, never shortened")}
                </FormHelperText>
              </FormControl>
            )}

            {!withoutNotice && (
              <ChangeValueFields
                event={event}
                estimatedValue={state.estimatedValue}
                frameworkMaximumValue={state.frameworkMaximumValue}
                isSaving={isSaving}
                errorOf={(field) => fieldError(field) ?? valueRefusals.find((refused) => refused.field === field)}
                onChange={(patch) => {
                  setValueRefusals([]);
                  updateField(patch, { debounceMs: 300 });
                }}
              />
            )}

            <FormControl>
              <FormLabel>
                {t("Description")} {isSaving("description") && <Spinner size="xs" />}
              </FormLabel>
              <TipTap
                content={state.description ?? ""}
                onUpdate={async (value) => updateField({ description: value })}
                editable={true}
                debounceWait={300}
              />
            </FormControl>

            <FormControl>
              <FormLabel>
                {t("Competition documents")} {isSaving("requestDocumentIds") && <Spinner size="xs" />}
              </FormLabel>
              {eventDocuments.length > 0 && (
                <Stack spacing="1" mb="3">
                  {eventDocuments.map((doc) => (
                    <Checkbox
                      key={doc.id}
                      isChecked={includedDocumentIds.includes(doc.id)}
                      onChange={(e) => toggleDocument(doc.id, e.target.checked)}
                    >
                      {doc.name}
                    </Checkbox>
                  ))}
                </Stack>
              )}
              {pendingUploads.length > 0 && (
                <Table variant="simple" size="sm" mb="3">
                  <Tbody>
                    {pendingUploads.map((upload) => (
                      <FileRow key={upload.uploadId} file={upload} name={upload.name} remove={removeUpload} />
                    ))}
                  </Tbody>
                </Table>
              )}
              <DropTarget
                targetId={`event-change-${draft.id}`}
                uppy={uppy}
                helperText={t("Drag and drop files here, or click to browse")}
              />
              <FormHelperText>
                {changesRequest
                  ? t("Checked documents are part of the request; new uploads are included automatically")
                  : t("Checked documents are part of the announcement; new uploads are included automatically")}
              </FormHelperText>
            </FormControl>

            {!privateContext && (
              <FormControl>
                <FormLabel>{t("Reason for the change")}</FormLabel>
                <RadioGroup
                  value={state.reasonCode ?? ""}
                  onChange={(value) => updateField({ reasonCode: value as ChangeReasonCode })}
                >
                  <Stack direction="row" spacing="6">
                    <Radio value={ChangeReasonCode.UpdateAdd}>{t("Updated information")}</Radio>
                    <Radio value={ChangeReasonCode.CorrectionBuyer}>{t("Correction")}</Radio>
                  </Stack>
                </RadioGroup>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>
                {t("What changed?")} {isSaving("changeDescription") && <Spinner size="xs" />}
              </FormLabel>
              <Textarea
                value={state.changeDescription ?? ""}
                onChange={(e) => updateField({ changeDescription: e.target.value }, { debounceMs: 300 })}
                placeholder={t("Describe the change for the suppliers") ?? undefined}
              />
              <FormHelperText>
                {withoutNotice
                  ? t("Shown to the invited suppliers with the change")
                  : t("Shown to suppliers and published with the change notice")}
              </FormHelperText>
            </FormControl>

            {deadline.canReopen && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The competition is closed for bids right now. Publishing the change reopens it until the new deadline you pick.",
                )}
              </Alert>
            )}

            {deadline.isClosed && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {event.changeGovernedTerm === ChangeGovernedTerm.RequestDeadline
                  ? t(
                      "The deadline for requests to participate has passed, so this change can no longer be published. Discard the change and continue to the invitation to tender.",
                    )
                  : withoutNotice
                    ? t("The bid deadline has passed, so this change can no longer be sent.")
                    : t(
                        "The bid deadline has passed, so this change can no longer be published. An announced competition closes at its deadline.",
                      )}
              </Alert>
            )}

            {deadline.warnsDeadlineIsNear && (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t(
                  "The current deadline is less than 24 hours away. The change takes effect only once Doffin confirms it.",
                )}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Flex justify="space-between" align="center" gap="4" w="full" wrap="wrap">
            <Text fontSize="sm" color="smMuted">
              {isSubmitting && !changesRequest
                ? t("Sending to Doffin can take up to a minute and a half. Keep this window open.")
                : changesRequest
                  ? t("{{count}} invited suppliers will be notified", { count: recipientCount })
                  : t("{{count}} registered suppliers will be notified", { count: recipientCount })}
            </Text>
            <Flex gap="3">
              <Button variant="ghost" onClick={confirmDiscard} isLoading={isCanceling} isDisabled={isSending}>
                {t("Discard change")}
              </Button>
              <Button
                colorScheme="teal"
                onClick={handleSubmit}
                isLoading={isSending}
                isDisabled={isCanceling || deadline.isClosed || noAnswer === "whileSending"}
              >
                {privateContext ? t("Publish the change") : withoutNotice ? t("Send the change") : t("Submit change notice")}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
    {discardConfirmation.modal}
    </>
  );
};

const fieldErrorsOf = (e: unknown): FieldErrorDto[] =>
  isFetchBaseQueryError(e) && isApiErrorDto(e.data) ? (e.data.fieldErrors ?? []) : [];

const valueRefusalsOf = (e: unknown): FieldErrorDto[] =>
  fieldErrorsOf(e).filter((error) => CHANGE_VALUE_FIELDS.includes(error.field));

const refusesOnlyValues = (e: unknown, refusedValues: FieldErrorDto[]): boolean =>
  refusedValues.length > 0 && refusedValues.length === fieldErrorsOf(e).length;
